import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { syncProgress } from '../utils/progressSync';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [corporate, setCorporate] = useState(undefined);

  const loadCorporate = async (authToken) => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/corporate/me`, { headers: { Authorization: `Bearer ${authToken}` } });
      const prev = JSON.parse(localStorage.getItem('bwgela_company') || '{}');
      localStorage.setItem('bwgela_company', JSON.stringify({ ...prev, name: data.name, linkId: data.link_id, theme: 'teal', learnerId: data.learner_id, learnerName: data.learner_name }));
      setCorporate(data);
      return data;
    } catch (e) {
      setCorporate(null);
      return null;
    }
  };

  useEffect(() => { if (user?.email) syncProgress().catch(() => {}); }, [user?.email]);

  // Set up axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${BACKEND_URL}/api/me`);
          setUser(response.data);
          loadCorporate(token);
        } catch (error) {
          console.error('Auth check failed:', error);
          logout();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token, BACKEND_URL]);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/login`, {
        username,
        password
      });

      const { access_token, user: userData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      
      toast({
        title: "Welcome back!",
        description: `Logged in as ${userData.username}`
      });

      return { success: true, corporate: await loadCorporate(access_token) };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast({
        title: "Login failed",
        description: message,
        variant: "destructive"
      });
      return { success: false, error: message };
    }
  };

  const register = async (username, email, password, fullName) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/register`, {
        username,
        email,
        password,
        full_name: fullName
      });

      const { access_token, user: userData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      
      toast({
        title: "Account created!",
        description: `Welcome to BWGELA, ${userData.username}!`
      });

      return { success: true, corporate: await loadCorporate(access_token) };
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast({
        title: "Registration failed",
        description: message,
        variant: "destructive"
      });
      return { success: false, error: message };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setCorporate(null);
    localStorage.removeItem('token');
    Object.keys(localStorage).filter((k) => k.startsWith('learning_') || k.startsWith('exec-mc-score-') || ['triviumVerbsProgress', 'exec-parts-done'].includes(k)).forEach((k) => localStorage.removeItem(k));
    delete axios.defaults.headers.common['Authorization'];
    
    // Also logout from Google session if exists
    axios.post(`${BACKEND_URL}/api/auth/google/logout`, {}, { withCredentials: true })
      .catch(err => console.error('Google logout error:', err));
    
    toast({
      title: "Logged out",
      description: "You have been successfully logged out."
    });
  };

  const loginWithGoogle = () => {
    // Redirect to Emergent Auth with proper redirect URL
    const redirectUrl = `${window.location.origin}/`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGoogleCallback = async () => {
    // Check if session_id exists in URL fragment
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      
      try {
        // Call backend to process session
        const response = await axios.post(
          `${BACKEND_URL}/api/auth/google/session`,
          {},
          {
            headers: { 'X-Session-ID': sessionId },
            withCredentials: true
          }
        );

        const { access_token, user: userData } = response.data;
        
        setToken(access_token);
        setUser(userData);
        localStorage.setItem('token', access_token);
        
        // Clean URL fragment
        window.history.replaceState(null, '', window.location.pathname);
        if (await loadCorporate(access_token)) window.location.assign('/my-aon');
        
        toast({
          title: "Welcome!",
          description: `Logged in as ${userData.username}`
        });

        return { success: true };
      } catch (error) {
        const message = error.response?.data?.detail || 'Google login failed';
        toast({
          title: "Login failed",
          description: message,
          variant: "destructive"
        });
        
        // Clean URL fragment even on error
        window.history.replaceState(null, '', window.location.pathname);
        
        return { success: false, error: message };
      }
    }
    return { success: false };
  };

  // Check for Google OAuth callback on mount
  useEffect(() => {
    handleGoogleCallback();
  }, []);

  const value = {
    user,
    token,
    login,
    register,
    logout,
    loginWithGoogle,
    loading,
    corporate,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};